'use es6';

import { requestCompleted } from 'redux-request-state/util/requestStateCheckers';
import { Component, Fragment } from 'react';
import { NavMarker } from 'react-rhumb';
import AsyncComponentContext from 'ContentEditorUI/context/AsyncComponentContext';
import { connect } from 'react-redux';
import I18n from 'I18n';
import formatName from 'I18n/utils/formatName';
import ContentRoutes from 'ContentUtils/Routes';
import UsageTracker from 'ContentEditorUI/utils/UsageTracker';
import { CURRENT_REVISION_ID } from 'ContentEditorUI/api/RevisionsApi';
import { buildRevisionPreviewUrl, buildScheduledUpdateRevisionPreviewUrl, buildStagingRevisionPreviewUrl } from 'ContentEditorUI/lib/preview/utils';
import { getContentModelId, getPreviewKey } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { getIsReadOnlyMode } from 'ContentEditorUI/redux/selectors/appStatusSelectors';
import { getLastAuthorId, getHasBufferedChanges, getLastSaveDate } from 'ContentEditorUI/redux/selectors/editingInfoSelectors';
import { getRevisionsError, getRevisionsWithScheduledUpdate, getScheduledRevision, getTotalRevisions } from 'ContentEditorUI/redux/selectors/revisionsSelectors';
import { getIsStagingV2 } from 'ContentEditorUI/redux/selectors/authSelectors';
import { fetchRevisions, selectRevision, fetchScheduledVersion } from 'ContentEditorUI/redux/actions/revisionsActions';
import { routeToPage } from 'ContentEditorUI/redux/actions/routingActions';
import { getIsPublished } from 'ContentEditorUI/redux/selectors/publishSelectors';
import { getUserForLastAuthor } from 'ContentEditorUI/redux/selectors/usersSelectors';
import { fetchUsers } from 'ContentEditorUI/redux/actions/userActions';
import { getPreviewDomain } from 'ContentEditorUI/js/redux/selectors/iframesSelectors';
import { getRevisionsRequest, getScheduledVersionRequest } from 'ContentEditorUI/redux/selectors/requestStatesSelectors';
import { clearSelectedItem } from 'ContentEditorUI/redux/actions/selectionActions';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PAGINATION_LIMIT = 15;

/**
 * @param {Object} props
 * @param {Function} props.selectRevision (required)
 * @param {Function} props.fetchRevisions (required)
 * @param {Function} props.fetchScheduledVersion (required)
 * @param {Function} props.routeToPage (required)
 * @param {number} props.contentId (required)
 * @param {boolean} props.disabled (required)
 * @param {Array} props.revisions (required)
 * @param {boolean} props.shouldRestoreToBuffer (required)
 * @param {number} props.totalRevisionsCount (required)
 * @param {Object} [props.lastBufferUser] (optional)
 * @param {boolean} props.isPublished (required)
 * @param {boolean} props.hasBufferedChanges (required)
 * @param {string} props.previewDomain (required)
 * @param {string} props.previewKey (required)
 * @param {number} [props.lastBufferAuthorId] (optional)
 * @param {Function} props.fetchUsers (required)
 * @param {Date} props.lastSave (required)
 * @param {Object} [props.scheduledRevision] (optional)
 * @param {Object} [props.scheduledRevisionRequestStatus] (optional)
 * @param {Object} [props.revisionsRequestStatus] (optional)
 * @param {Function} props.clearSelectedItem (required)
 * @param {Function} [props.onClose] (optional)
 */
class RevisionsModalContainer extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      revisionOffset: 0
    };
    this.handleLoadMoreRevisions = () => {
      this.props.fetchRevisions(this.state.revisionOffset, PAGINATION_LIMIT);
      this.setState({
        revisionOffset: this.state.revisionOffset + PAGINATION_LIMIT
      });
    };
    this.handleClose = () => {
      if (this.props.onClose) {
        this.props.onClose();
      } else {
        this.props.clearSelectedItem();
        this.props.routeToPage('content');
      }
    };
    this.selectRevision = revision => {
      const {
        shouldRestoreToBuffer
      } = this.props;
      this.props.selectRevision(revision.id, {
        shouldRestoreToBuffer,
        shouldRestoreScheduledUpdateToBuffer: !!revision.isScheduledUpdate
      });
      this.handleClose();
    };
    this.getPreviewUrl = (selectedRevision, isBuffer) => {
      const {
        contentId,
        previewDomain,
        previewKey,
        extraPreviewParams,
        isStagingV2
      } = this.props;
      const extraParams = extraPreviewParams || {};
      if (isStagingV2) {
        if (isBuffer) {
          return buildStagingRevisionPreviewUrl(previewDomain, contentId, CURRENT_REVISION_ID, extraParams);
        }
        return buildStagingRevisionPreviewUrl(previewDomain, contentId, selectedRevision.id, extraParams);
      }
      if (isBuffer) {
        return ContentRoutes.getPreviewUrl(contentId, Object.assign({
          preview_key: previewKey,
          isPreview: true
        }, extraParams), previewDomain);
      }
      if (selectedRevision.isScheduledUpdate) {
        return buildScheduledUpdateRevisionPreviewUrl(previewDomain, contentId, selectedRevision.id, extraParams);
      }
      return buildRevisionPreviewUrl(previewDomain, contentId, selectedRevision.id, extraParams);
    };
    this.getBufferAuthorName = () => {
      const {
        lastBufferAuthorId,
        lastBufferUser
      } = this.props;
      if (lastBufferUser && lastBufferUser.fullName) {
        return lastBufferUser.fullName;
      } else if (lastBufferUser) {
        return formatName({
          firstName: lastBufferUser.firstName,
          lastName: lastBufferUser.lastName
        });
      }
      return I18n.text('app.unknownUserWithId', {
        unknownUserId: lastBufferAuthorId.toString()
      });
    };
  }
  componentDidMount() {
    this.props.fetchScheduledVersion();
    this.handleLoadMoreRevisions();
  }
  render() {
    const {
      disabled,
      revisions,
      shouldRestoreToBuffer,
      totalRevisionsCount,
      isPublished,
      hasBufferedChanges,
      lastSave,
      scheduledRevision,
      scheduledRevisionRequestStatus,
      revisionsRequestStatus,
      themeRevisionsConfig,
      hasTabs,
      contentType,
      isStagingV2
    } = this.props;
    const revisionsLeftToLoad = Math.max(totalRevisionsCount - this.state.revisionOffset - (scheduledRevision ? 1 : 0), 0);
    const paginationLimit = revisionsLeftToLoad < PAGINATION_LIMIT ? revisionsLeftToLoad : PAGINATION_LIMIT;
    if (hasBufferedChanges) {
      this.props.fetchUsers();
    }
    return /*#__PURE__*/_jsx(AsyncComponentContext.Consumer, {
      children: ({
        BaymaxRevisionsModal
      }) => {
        if (hasTabs) {
          const revisionTabsConfigs = {
            page: {
              onLoadMoreRevisions: this.handleLoadMoreRevisions,
              onChooseRevision: this.selectRevision,
              onClose: this.handleClose,
              getPreviewUrl: this.getPreviewUrl,
              disabled,
              hasMoreRevisions: !!revisionsLeftToLoad,
              paginationLimit,
              revisions,
              totalRevisionCount: totalRevisionsCount,
              shouldRestoreToBuffer,
              isLoadingInitialRevisions:
              // following condition checks if we are loading the first page of
              // revisions (indicated by revisionOffset === 0) to show loading spinner when modal opens
              this.state.revisionOffset === 0 && !requestCompleted(revisionsRequestStatus) || !requestCompleted(scheduledRevisionRequestStatus),
              isPublished,
              hasBufferedChanges: isStagingV2 ? false : hasBufferedChanges,
              lastBufferAuthor: this.getBufferAuthorName(),
              lastSave,
              //TODO: for first release we only support page but in future consumer should pass title
              title: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "components.modals.revisionsViewModal.defaultTitle"
              })
            },
            theme: themeRevisionsConfig
          };
          return /*#__PURE__*/_jsxs(Fragment, {
            children: [/*#__PURE__*/_jsx(BaymaxRevisionsModal, {
              revisionTabsConfigs: revisionTabsConfigs,
              usageTracker: UsageTracker.getCachedClonedTracker('revisions'),
              onClose: this.handleClose,
              contentType: contentType,
              hasTabs: hasTabs,
              defaultTabId: "page"
            }), /*#__PURE__*/_jsx(NavMarker, {
              name: "REVISIONS_MODAL_RENDERED"
            })]
          });
        }
        return /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(BaymaxRevisionsModal, {
            onLoadMoreRevisions: this.handleLoadMoreRevisions,
            onChooseRevision: this.selectRevision,
            onClose: this.handleClose,
            getPreviewUrl: this.getPreviewUrl,
            disabled: disabled,
            hasMoreRevisions: !!revisionsLeftToLoad,
            paginationLimit: paginationLimit,
            revisions: revisions,
            totalRevisionCount: totalRevisionsCount,
            shouldRestoreToBuffer: shouldRestoreToBuffer,
            usageTracker: UsageTracker.getCachedClonedTracker('revisions'),
            isLoadingInitialRevisions:
            // following condition checks if we are loading the first page of
            // revisions (indicated by revisionOffset === 0) to show loading spinner when modal opens
            this.state.revisionOffset === 0 && !requestCompleted(revisionsRequestStatus) || !requestCompleted(scheduledRevisionRequestStatus),
            isPublished: isPublished,
            hasBufferedChanges: isStagingV2 ? false : hasBufferedChanges,
            lastBufferAuthor: this.getBufferAuthorName(),
            lastSave: lastSave
          }), /*#__PURE__*/_jsx(NavMarker, {
            name: "REVISIONS_MODAL_RENDERED"
          })]
        });
      }
    });
  }
}
const mapStateToProps = state => {
  return {
    revisionsError: getRevisionsError(state),
    contentId: getContentModelId(state),
    disabled: getIsReadOnlyMode(state),
    shouldRestoreToBuffer: getIsPublished(state),
    totalRevisionsCount: getTotalRevisions(state),
    revisions: getRevisionsWithScheduledUpdate(state),
    isPublished: getIsPublished(state),
    hasBufferedChanges: getHasBufferedChanges(state),
    previewDomain: getPreviewDomain(state),
    previewKey: getPreviewKey(state),
    lastBufferAuthorId: getLastAuthorId(state),
    lastBufferUser: getUserForLastAuthor(state),
    lastSave: getLastSaveDate(state),
    scheduledRevision: getScheduledRevision(state),
    scheduledRevisionRequestStatus: getScheduledVersionRequest(state),
    revisionsRequestStatus: getRevisionsRequest(state),
    isStagingV2: getIsStagingV2(state)
  };
};
const mapDispatchToProps = {
  fetchRevisions,
  fetchScheduledVersion,
  selectRevision,
  routeToPage,
  fetchUsers,
  clearSelectedItem
};
const ConnectedComponent = connect(mapStateToProps, mapDispatchToProps)(RevisionsModalContainer);
export default ConnectedComponent;